import { BenchmarkRunner } from "./benchmark";
import { exportCsv, exportJson, downloadText } from "./export";
import {
  loadHostnames,
  loadResolvers,
  resetHostnames,
  resetResolvers,
  saveHostnames,
  saveResolvers
} from "./storage";
import "./styles.css";
import type { BenchmarkProgress, BenchmarkSnapshot, Resolver, ResolverResult } from "./types";
import { normalizeHostname, normalizeHostnames, validateEndpoint } from "./validation";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root was not found.");
const appRoot = app;

type State = {
  resolvers: Resolver[];
  hostnames: string[];
  results: ResolverResult[];
  progress?: BenchmarkProgress;
  message: string;
  runner: BenchmarkRunner;
  snapshot?: BenchmarkSnapshot;
};

const state: State = {
  resolvers: loadResolvers(),
  hostnames: loadHostnames(),
  results: [],
  message: navigator.onLine
    ? ""
    : "You appear to be offline. Benchmark requests need network access.",
  runner: new BenchmarkRunner()
};

window.addEventListener("online", () => {
  state.message = "";
  render();
});
window.addEventListener("offline", () => {
  state.message = "You appear to be offline. Benchmark requests need network access.";
  render();
});

function render(): void {
  appRoot.innerHTML = `
    <main class="shell">
      <section class="hero">
        <div>
          <p class="eyebrow">Client-side DNS-over-HTTPS test</p>
          <h1>DNS Resolver Benchmark</h1>
          <p class="lede">
            Measure browser DoH request latency from this device. Requests originate locally in your browser and include HTTP, TLS, network, and resolver overhead.
          </p>
        </div>
        <div class="run-panel">
          <label>Timeout <input id="timeout" type="number" min="500" step="500" value="5000" /> ms</label>
          <label>Resolver concurrency <input id="concurrency" type="number" min="1" max="6" value="3" /></label>
          <button id="run" class="primary" ${state.runner.isRunning() ? "disabled" : ""}>Run benchmark</button>
          <button id="cancel" ${state.runner.isRunning() ? "" : "disabled"}>Cancel</button>
        </div>
      </section>

      ${state.message ? `<p class="notice" role="status">${escapeHtml(state.message)}</p>` : ""}
      ${renderProgress()}

      <section class="grid">
        ${renderResolverPanel()}
        ${renderDomainPanel()}
      </section>

      <section class="results-section" aria-labelledby="results-title">
        <div class="section-head">
          <h2 id="results-title">Results</h2>
          <div class="actions">
            <button id="download-json" ${state.snapshot ? "" : "disabled"}>Download JSON</button>
            <button id="download-csv" ${state.snapshot ? "" : "disabled"}>Download CSV</button>
          </div>
        </div>
        ${renderResults()}
        ${renderBars()}
      </section>

      <section class="explain">
        <h2>How measurement works</h2>
        <p>
          This application sends DNS-over-HTTPS requests directly from your browser to the selected resolver endpoints. It does not proxy DNS lookups, keep accounts, run analytics, or upload exports.
        </p>
        <p>
          Results are location- and network-specific. Browser HTTP/TLS work, connection reuse, operating-system behavior, resolver caches, and network caches are part of the measured browser DoH request latency, so repeated runs can vary.
        </p>
        <p>
          Browser CORS rules can block individual DoH providers. A resolver is only marked CORS blocked after your browser rejects an attempted request.
          For broader comparison, the app can time an opaque no-cors fallback after a browser-blocked request, but those rows cannot prove the DNS response body was valid.
        </p>
      </section>
    </main>
  `;

  bindEvents();
}

function renderProgress(): string {
  const progress = state.progress;
  if (!progress) return "";
  return `
    <section class="progress" role="status" aria-live="polite">
      <div><span>Resolver</span><strong>${escapeHtml(progress.currentResolver ?? "Waiting")}</strong></div>
      <div><span>Resolvers</span><strong>${progress.completedResolvers} / ${progress.totalResolvers}</strong></div>
      <div><span>Query</span><strong>${escapeHtml(progress.currentHostname ?? `${progress.completedQueries} / ${progress.totalQueries}`)}</strong></div>
      <div><span>Elapsed</span><strong>${formatMs(progress.elapsedMs)}</strong></div>
    </section>
  `;
}

function renderResolverPanel(): string {
  return `
    <section class="panel" aria-labelledby="resolvers-title">
      <div class="section-head">
        <h2 id="resolvers-title">Resolvers</h2>
        <button id="reset-resolvers">Reset</button>
      </div>
      <div class="list">
        ${state.resolvers
          .map(
            (resolver) => `
          <label class="row-item">
            <input type="checkbox" data-resolver-toggle="${resolver.id}" ${resolver.enabled ? "checked" : ""} />
            <span>
              <strong>${escapeHtml(resolver.name)}</strong>
              <small>${endpointHost(resolver.endpoint)}</small>
              ${resolver.notes ? `<small class="resolver-note">${escapeHtml(resolver.notes)}</small>` : ""}
            </span>
            ${resolver.custom ? `<button data-remove-resolver="${resolver.id}" type="button">Remove</button>` : ""}
          </label>`
          )
          .join("")}
      </div>
      <form id="resolver-form" class="form">
        <input name="name" placeholder="Custom resolver name" aria-label="Custom resolver name" />
        <input name="endpoint" placeholder="https://resolver.example/dns-query" aria-label="Custom resolver endpoint" />
        <button>Add resolver</button>
      </form>
      <div class="actions">
        <button id="export-resolvers">Export JSON</button>
        <label class="file-button">Import JSON<input id="import-resolvers" type="file" accept="application/json" /></label>
      </div>
      <p class="hint">Custom endpoints must use HTTPS and still need browser CORS support when the benchmark runs.</p>
    </section>
  `;
}

function renderDomainPanel(): string {
  return `
    <section class="panel" aria-labelledby="domains-title">
      <div class="section-head">
        <h2 id="domains-title">Test domains</h2>
        <button id="reset-hostnames">Reset</button>
      </div>
      <div class="chips">
        ${state.hostnames
          .map(
            (hostname) => `
          <span class="chip">
            <input value="${escapeAttribute(hostname)}" data-hostname-edit="${escapeAttribute(hostname)}" aria-label="Edit ${escapeAttribute(hostname)}" />
            <button data-remove-hostname="${escapeAttribute(hostname)}" aria-label="Remove ${escapeAttribute(hostname)}">×</button>
          </span>`
          )
          .join("")}
      </div>
      <form id="hostname-form" class="form compact">
        <input name="hostname" placeholder="Add domain" aria-label="Add test domain" />
        <button>Add</button>
      </form>
      <div class="actions">
        <button id="export-hostnames">Export JSON</button>
        <label class="file-button">Import JSON<input id="import-hostnames" type="file" accept="application/json" /></label>
      </div>
    </section>
  `;
}

function renderResults(): string {
  const results = sortedResults();
  if (!results.length)
    return `<p class="empty">Run a benchmark to populate resolver results progressively.</p>`;

  return `
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Resolver</th><th>Status</th><th>Minimum</th><th>Median</th><th>Average</th><th>P95</th><th>Maximum</th><th>Success rate</th><th>Details</th>
          </tr>
        </thead>
        <tbody>
          ${results
            .map(
              (result) => `
            <tr>
              <td><strong>${escapeHtml(result.resolverName)}</strong><small>${escapeHtml(endpointHost(result.endpoint))}</small></td>
              <td><span class="status ${result.status}">${statusLabel(result.status)}</span></td>
              <td>${formatOptionalMs(result.minMs)}</td>
              <td>${formatOptionalMs(result.medianMs)}</td>
              <td>${formatOptionalMs(result.averageMs)}</td>
              <td>${formatOptionalMs(result.p95Ms)}</td>
              <td>${formatOptionalMs(result.maxMs)}</td>
              <td>${formatPercent(result.successRate)}</td>
              <td><button data-toggle-details="${result.resolverId}">Toggle</button></td>
            </tr>
            <tr class="details-row" id="details-${result.resolverId}" hidden>
              <td colspan="9">${renderDetails(result)}</td>
            </tr>`
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderDetails(result: ResolverResult): string {
  if (!result.queryResults.length) return `<p class="hint">No measured queries were collected.</p>`;
  return `
    <div class="detail-grid">
      ${result.queryResults
        .map(
          (query) => `
        <div>
          <strong>${escapeHtml(query.hostname)}</strong>
          <span>${query.status}</span>
          <span>${formatOptionalMs(query.durationMs)}</span>
          <span>${query.opaque ? `${query.methodUsed ?? "GET"} no-cors` : (query.methodUsed ?? "—")}</span>
          <small>${escapeHtml(query.errorMessage ?? failureText(query.status))}</small>
        </div>`
        )
        .join("")}
    </div>
  `;
}

function renderBars(): string {
  const completed = sortedResults().filter((result) => typeof result.medianMs === "number");
  if (!completed.length) return "";
  const max = Math.max(...completed.map((result) => result.medianMs as number));
  return `
    <div class="bars" aria-label="Median latency bars">
      ${completed
        .map(
          (result) => `
        <div class="bar-row">
          <span>${escapeHtml(result.resolverName)}</span>
          <div class="bar-track"><div class="bar" style="width:${Math.max(4, ((result.medianMs as number) / max) * 100)}%"></div></div>
          <strong>${formatOptionalMs(result.medianMs)}</strong>
        </div>`
        )
        .join("")}
    </div>
  `;
}

function bindEvents(): void {
  document.querySelector("#run")?.addEventListener("click", runBenchmark);
  document.querySelector("#cancel")?.addEventListener("click", () => state.runner.cancel());
  document.querySelector("#download-json")?.addEventListener("click", () => {
    if (state.snapshot)
      downloadText("dns-benchmark-results.json", exportJson(state.snapshot), "application/json");
  });
  document.querySelector("#download-csv")?.addEventListener("click", () => {
    if (state.snapshot)
      downloadText("dns-benchmark-results.csv", exportCsv(state.snapshot), "text/csv");
  });

  document.querySelectorAll<HTMLInputElement>("[data-resolver-toggle]").forEach((input) => {
    input.addEventListener("change", () => {
      state.resolvers = state.resolvers.map((resolver) =>
        resolver.id === input.dataset.resolverToggle
          ? { ...resolver, enabled: input.checked }
          : resolver
      );
      saveResolvers(state.resolvers);
    });
  });

  document.querySelectorAll<HTMLButtonElement>("[data-remove-resolver]").forEach((button) => {
    button.addEventListener("click", () => {
      state.resolvers = state.resolvers.filter(
        (resolver) => resolver.id !== button.dataset.removeResolver
      );
      saveResolvers(state.resolvers);
      render();
    });
  });

  document.querySelector("#resolver-form")?.addEventListener("submit", addResolver);
  document.querySelector("#hostname-form")?.addEventListener("submit", addHostname);
  document.querySelector("#reset-resolvers")?.addEventListener("click", () => {
    state.resolvers = resetResolvers();
    render();
  });
  document.querySelector("#reset-hostnames")?.addEventListener("click", () => {
    state.hostnames = resetHostnames();
    render();
  });
  document.querySelector("#export-resolvers")?.addEventListener("click", () => {
    downloadText(
      "dns-resolvers.json",
      JSON.stringify(state.resolvers, null, 2),
      "application/json"
    );
  });
  document.querySelector("#export-hostnames")?.addEventListener("click", () => {
    downloadText(
      "dns-hostnames.json",
      JSON.stringify(state.hostnames, null, 2),
      "application/json"
    );
  });
  document.querySelector("#import-resolvers")?.addEventListener("change", importResolvers);
  document.querySelector("#import-hostnames")?.addEventListener("change", importHostnames);
  document.querySelectorAll<HTMLInputElement>("[data-hostname-edit]").forEach((input) => {
    input.addEventListener("change", () => editHostname(input));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-remove-hostname]").forEach((button) => {
    button.addEventListener("click", () => {
      state.hostnames = state.hostnames.filter(
        (hostname) => hostname !== button.dataset.removeHostname
      );
      saveHostnames(state.hostnames);
      render();
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-toggle-details]").forEach((button) => {
    button.addEventListener("click", () => {
      const row = document.querySelector<HTMLTableRowElement>(
        `#details-${button.dataset.toggleDetails}`
      );
      if (row) row.hidden = !row.hidden;
    });
  });
}

async function runBenchmark(): Promise<void> {
  const enabled = state.resolvers.filter((resolver) => resolver.enabled);
  const domains = normalizeHostnames(state.hostnames);
  if (!enabled.length) {
    state.message = "Enable at least one resolver.";
    render();
    return;
  }
  if (!domains.ok) {
    state.message = domains.message;
    render();
    return;
  }

  const timeoutMs = Number(document.querySelector<HTMLInputElement>("#timeout")?.value ?? 5000);
  const resolverConcurrency = Number(
    document.querySelector<HTMLInputElement>("#concurrency")?.value ?? 3
  );
  const startedAt = new Date().toISOString();
  state.results = enabled.map((resolver) => ({
    resolverId: resolver.id,
    resolverName: resolver.name,
    endpoint: resolver.endpoint,
    status: "pending",
    queryResults: []
  }));
  state.snapshot = undefined;
  render();

  const results = await state.runner.run(
    enabled,
    domains.value,
    { timeoutMs, resolverConcurrency },
    {
      onProgress(progress) {
        state.progress = progress;
        render();
      },
      onResult(result) {
        state.results = state.results.map((existing) =>
          existing.resolverId === result.resolverId ? result : existing
        );
        render();
      }
    }
  );

  state.results = state.results.map(
    (existing) => results.find((result) => result.resolverId === existing.resolverId) ?? existing
  );
  state.snapshot = {
    startedAt,
    finishedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    timeoutMs,
    hostnames: domains.value,
    resolvers: enabled,
    results: state.results,
    cancelled: Boolean(state.progress?.cancelled)
  };
  render();
}

function addResolver(event: Event): void {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);
  const endpoint = validateEndpoint(String(data.get("endpoint") ?? ""));
  if (!endpoint.ok) {
    state.message = `${endpoint.message} CORS support is required for browser testing.`;
    render();
    return;
  }
  const name = String(data.get("name") ?? "").trim();
  if (!name) {
    state.message = "Resolver name is required.";
    render();
    return;
  }
  state.resolvers = [
    ...state.resolvers,
    {
      id: `custom-${crypto.randomUUID()}`,
      name,
      endpoint: endpoint.value,
      preferredMethod: "AUTO",
      enabled: true,
      custom: true,
      notes: "Custom endpoint. Browser CORS support will be confirmed during benchmark."
    }
  ];
  state.message = "";
  saveResolvers(state.resolvers);
  render();
}

function addHostname(event: Event): void {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);
  const result = normalizeHostname(String(data.get("hostname") ?? ""));
  if (!result.ok) {
    state.message = result.message;
    render();
    return;
  }
  state.hostnames = [...new Set([...state.hostnames, result.value])];
  state.message = "";
  saveHostnames(state.hostnames);
  render();
}

function editHostname(input: HTMLInputElement): void {
  const previous = input.dataset.hostnameEdit;
  const result = normalizeHostname(input.value);
  if (!previous || !result.ok) {
    state.message = result.ok ? "" : result.message;
    render();
    return;
  }
  state.hostnames = [
    ...new Set(state.hostnames.map((hostname) => (hostname === previous ? result.value : hostname)))
  ];
  saveHostnames(state.hostnames);
  render();
}

async function importResolvers(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const parsed = JSON.parse(await file.text()) as Resolver[];
  state.resolvers = parsed.map((resolver) => ({ ...resolver, enabled: Boolean(resolver.enabled) }));
  saveResolvers(state.resolvers);
  render();
}

async function importHostnames(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const parsed = JSON.parse(await file.text()) as string[];
  const result = normalizeHostnames(parsed);
  if (!result.ok) {
    state.message = result.message;
  } else {
    state.hostnames = result.value;
    saveHostnames(state.hostnames);
  }
  render();
}

function sortedResults(): ResolverResult[] {
  return [...state.results].sort((a, b) => {
    if (a.status !== "complete" || b.status !== "complete") return 0;
    return (a.medianMs ?? Number.POSITIVE_INFINITY) - (b.medianMs ?? Number.POSITIVE_INFINITY);
  });
}

function endpointHost(endpoint: string): string {
  return new URL(endpoint).hostname;
}

function formatOptionalMs(value?: number): string {
  return typeof value === "number" ? `${Math.round(value)} ms` : "—";
}

function formatMs(value: number): string {
  return `${(value / 1000).toFixed(1)} s`;
}

function formatPercent(value?: number): string {
  return typeof value === "number" ? `${Math.round(value * 100)}%` : "—";
}

function statusLabel(status: string): string {
  return status.replace("_", " ");
}

function failureText(status: string): string {
  if (status === "cors_blocked")
    return "The browser blocked access, likely because the endpoint does not permit this origin.";
  if (status === "opaque")
    return "Opaque no-cors request completed, but the browser did not expose the DNS response.";
  if (status === "timeout") return "The request exceeded the configured timeout.";
  if (status === "cancelled") return "The benchmark was cancelled before this request completed.";
  return "";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}

render();
